import React, { useCallback, useMemo } from 'react'
import { SceneVideoUser } from 'video-conference-media'
import styled from '@emotion/styled'
import { Config } from 'common/config'
import { ui } from '@beelday/common'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useSelector } from 'react-redux'
import { RootState } from 'common/redux'
import { getListPollingApi } from './list-polling-api'
import ListPollingChat from './list-polling-chat'
import { Answer } from './list-polling-models'
import { isEqual, uniq } from 'lodash'
import OnlyAudioGrid from 'video-conference-media/presentation/only-audio-grid'
import ThreeColumnsVideoGrid from 'room/training-room/ice-breaker/presentation/three-columns-video-grid'
import { ArchivedActivityCard } from 'features/script'
import { navigateToRecaps } from 'workflow/navigate-to-recaps'
import { useSessionMode } from 'session-mode/use-session-mode'
import { useAssertedWorkflowUser } from 'room/common/use-workflow-user'
import useFetchMissingProfiles from 'users/use-fetch-missing-profiles'
import { useProfilesFor } from 'users/redux'
import { ListPollingStageView } from './list-polling-stage-view'
import { ListAnswerField } from './list-answer-field'
import { useStageReadByTheRoom } from '../stage/use-stage-read-by-the-room'

/** The stage's whole width, the foot on the word cloud's line. */
const InPersonList = styled.div`
	display: flex;
	flex: 1;
	margin-bottom: 20px;
	min-height: 0;
	width: 100%;
`

type Props = {
	streams: SceneVideoUser[]
	isTrainer: boolean
}

const ListPollingPage = ({ streams, isTrainer }: Props): JSX.Element | null => {
	const listPollingApi = getListPollingApi(Config.beeldayBackendUrl)
	const roomAddress = useAssertedJoinedRoomAddress()
	const pollingDefinition = useSelector(
		(state: RootState) => state.listPolling.pollingDefinition
	)

	const answers = useSelector((state: RootState) => state.listPolling.answers)
	const training = useSelector((state: RootState) => state.sessionInfo.training)
	const inPerson = useSessionMode() === 'IN_PERSON'
	const readByTheRoom = useStageReadByTheRoom()
	const me = useAssertedWorkflowUser()
	const authorIds = useMemo(
		() =>
			uniq(
				Object.values(answers)
					.flat()
					.map(answer => answer.userId)
			),
		[answers]
	)
	useFetchMissingProfiles(authorIds)
	const authorProfiles = useProfilesFor(authorIds)
	const authors = useMemo(
		() =>
			new Map(
				authorProfiles.flatMap(profile =>
					profile ? [[profile.id, profile] as const] : []
				)
			),
		[authorProfiles]
	)

	const onSubmit = useCallback(
		(questionId, text) => {
			if (pollingDefinition) {
				listPollingApi.sendAnswer(
					roomAddress,
					pollingDefinition.id,
					questionId,
					text
				)
			}
		},
		[listPollingApi, pollingDefinition, roomAddress]
	)

	if (!pollingDefinition) return null

	if (pollingDefinition.doneInPreviousSession) {
		return (
			<ArchivedActivityCard
				title={pollingDefinition.name}
				toolType="POLLING_OPEN_ENDED_LIST"
				representativeQuestion={pollingDefinition.questions[0]?.text}
				isTrainer={isTrainer}
				onSeeInRecaps={
					training
						? () => navigateToRecaps({ trainingId: training.id, isTrainer })
						: undefined
				}
			/>
		)
	}

	/*
	 * In person the room answers on its own devices and reads the answers
	 * from the trainer's stage: large wherever the room reads it, the
	 * trainer's own field never on the projector. The console of a
	 * presentation keeps the list as it is online.
	 */
	if (inPerson && isTrainer && readByTheRoom) {
		const { allowForMultipleAnswer, letFacilitatorAnswer } = pollingDefinition
		return (
			<ListPollingStageView
				name={pollingDefinition.name}
				questions={pollingDefinition.questions.map(question => ({
					id: question.id,
					text: question.text,
					answers: (answers[question.id] ?? []).map(answer => ({
						text: answer.text,
						author: authors.get(answer.userId),
					})),
				}))}
				answerField={
					letFacilitatorAnswer
						? questionId => (
								<ListAnswerField
									questionId={questionId}
									answerGiven={
										!allowForMultipleAnswer &&
										!!answers[questionId]?.some(
											answer => answer.userId === me.id
										)
									}
									onSubmit={onSubmit}
								/>
						  )
						: undefined
				}
			/>
		)
	}

	const chats = (
		<ui.ListPollingMainContainer>
			{pollingDefinition.questions.map(question => (
				<MemoizedListPollingChat
					key={question.id}
					question={question}
					isTrainer={isTrainer}
					answers={answers[question.id]}
					letFacilitatorAnswer={pollingDefinition.letFacilitatorAnswer}
					allowForMultipleAnswer={pollingDefinition.allowForMultipleAnswer}
					onSubmit={(questionId, text) => onSubmit(questionId, text)}
				/>
			))}
		</ui.ListPollingMainContainer>
	)

	// In person there are no cameras to make room for: the stage's whole width.
	if (inPerson) {
		return <InPersonList>{chats}</InPersonList>
	}

	return pollingDefinition.questions.length > 2 ? (
		<OnlyAudioGrid sceneVideoUsers={streams}>{chats}</OnlyAudioGrid>
	) : (
		<ThreeColumnsVideoGrid sceneVideoUsers={streams}>
			{chats}
		</ThreeColumnsVideoGrid>
	)
}

function chatPropsAreEqual(
	prevAnswers: { answers?: Answer[] },
	newAnswers: { answers?: Answer[] }
): boolean {
	return isEqual(prevAnswers.answers, newAnswers.answers)
}

const MemoizedListPollingChat = React.memo(ListPollingChat, chatPropsAreEqual)

export default ListPollingPage
